import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, QUOTE_PATH, CONTACT } from "../../lib/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled || open ? "bg-white/95 shadow-[0_1px_0_rgba(10,46,32,0.08)]" : "bg-white/70"} backdrop-blur-xl`}>
      <div className="container-x flex h-[76px] items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="Mega Solar — Início"><Logo /></Link>
        <nav aria-label="Navegação principal" className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.to.replace(/\//g, "") || "inicio"}`}
              className={({ isActive }) => `nav-link ${isActive ? "text-forest after:scale-x-100" : "text-ink/75 hover:text-forest"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to={QUOTE_PATH} data-testid="header-quote-button" className="btn-gold hidden sm:inline-flex !py-2.5 !px-5 group">
            Pedir Orçamento <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls="mobile-menu"
            aria-label={open ? "Fechar menu" : "Abrir menu"} data-testid="mobile-menu-toggle"
            className="xl:hidden grid h-11 w-11 place-items-center rounded-full border border-forest/15 text-forest hover:bg-forest hover:text-white transition-colors">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div id="mobile-menu" data-testid="mobile-menu" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-forest/10 bg-white">
            <nav aria-label="Navegação móvel" className="container-x flex flex-col py-6">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink to={n.to} end data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "inicio"}`}
                    className={({ isActive }) => `block border-b border-forest/10 py-4 font-display text-2xl tracking-tight ${isActive ? "text-forest" : "text-ink/80"}`}>
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link to={QUOTE_PATH} className="btn-gold" data-testid="mobile-quote-button">Pedir Orçamento Gratuito</Link>
                <a href={CONTACT.phoneHref} className="btn-outline" data-testid="mobile-call-button"><Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}</a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
